/* eslint-disable react/jsx-no-bind */
import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { format, parseISO } from 'date-fns';
import {
  Avatar,
  Button,
  InputNumber,
  Modal,
  Divider,
  Loader,
  Tag,
  TagGroup,
} from 'rsuite';
import { toast } from 'react-toastify';
import { DarkModeContext } from '~/common/contexts/darkMode';
import { ButtonBorderBlue } from '~/components/Buttons/ButtonBorderBlue/styles';
import { ButtonFullBlue } from '~/components/Buttons/ButtonFullBlue/styles';
import { InputDataRange } from '~/components/InputDataRange';
import SmartLinkRange from '~/components/SmartLinkRange';
import api from '~/services/api';
import FiltroCidades from '../../../Mensagens/components/FiltroCidades';
import FiltroEstabelecimentos from '../../../Mensagens/components/FiltroEstabelecimentos';
import FiltroProfissionais from '../../../Mensagens/components/FiltroProfissionais';

const locale = {
  sunday: 'Dom',
  monday: 'Seg',
  tuesday: 'Ter',
  wednesday: 'Qua',
  thursday: 'Qui',
  friday: 'Sex',
  saturday: 'Sab',
  ok: 'Ok',
  today: 'Hoje',
  yesterday: 'Ontem',
  hours: 'Horas',
  minutes: 'Minutos',
  seconds: 'Segundos',
};

const opcoesSimNao = [
  { label: 'Todos', value: 'todos' },
  { label: 'Sim', value: 'sim' },
  { label: 'Não', value: 'nao' },
];

function formatarData(valor) {
  if (!valor) {
    return '-';
  }

  return format(parseISO(valor), 'dd/MM/yyyy');
}

function formatarHora(valor) {
  if (!valor) {
    return '-';
  }

  return format(new Date(valor), 'HH:mm');
}

function valorComFallback(valor, fallback = '-') {
  if (valor === null || valor === undefined || valor === '') {
    return fallback;
  }

  return valor;
}

function numeroSeguro(valor, fallback = 0) {
  const numero = Number(valor);

  if (Number.isNaN(numero)) {
    return fallback;
  }

  return numero;
}

function obterResumoNota(nota) {
  const valor = numeroSeguro(nota);

  if (valor >= 4.8) return 'Excelente';
  if (valor >= 4.5) return 'Muito bom';
  if (valor >= 4) return 'Bom';

  return 'Regular';
}

function obterResumoFaltas(faltas) {
  const valor = numeroSeguro(faltas);

  if (valor === 0) return 'Excelente';
  if (valor === 1) return 'Bom';

  return 'Atenção';
}

function obterResumoCancelamentos(cancelamentos) {
  const valor = numeroSeguro(cancelamentos);

  if (valor === 0) return 'Excelente';
  if (valor === 1) return 'Bom';

  return 'Atenção';
}

function obterResumoDistancia(distanciaKm) {
  if (distanciaKm === null || distanciaKm === undefined || distanciaKm === '') {
    return 'Sem cálculo';
  }

  const valor = numeroSeguro(distanciaKm, null);

  if (valor === null) return 'Sem cálculo';
  if (valor <= 2) return 'Muito perto';
  if (valor <= 5) return 'Próximo';

  return 'Disponível';
}

function obterResumoTrabalhos(shifts) {
  const valor = numeroSeguro(shifts);

  if (valor === 1) return '1 serviço realizado';

  return `${valor} serviços realizados`;
}

function obterConfiguracaoMatch(pontuacao) {
  const valor = Math.max(0, Math.min(100, numeroSeguro(pontuacao)));

  if (valor >= 95) {
    return {
      valor: Math.round(valor),
      cor: '#16a34a',
      fundo: '#dcfce7',
      descricao: 'Excelente match',
    };
  }

  if (valor >= 90) {
    return {
      valor: Math.round(valor),
      cor: '#16a34a',
      fundo: '#dcfce7',
      descricao: 'Ótimo match',
    };
  }

  return {
    valor: Math.round(valor),
    cor: '#16a34a',
    fundo: '#dcfce7',
    descricao: 'Bom match',
  };
}

const resultadoInicial = {
  resumo: {
    totalRegistros: 0,
    totalMatches: 0,
    totalSemMatch: 0,
  },
  matches: [],
  semMatch: [],
};

const coresLinkAutomatico = {
  azulPrincipal: '#1d4ed8',
  azulEscuro: '#1e3a8a',
  azulSuave: '#eff6ff',
  verdePrincipal: '#16a34a',
  verdeEscuro: '#15803d',
  verdeClaro: '#dcfce7',
  verdeBorda: '#86efac',
  verdeSuave: '#f0fdf4',
  bordaSuave: '#e2e8f0',
  textoSuave: '#6b7280',
};

function adicionarItemFiltro(item, setState) {
  setState((estadoAnterior) => {
    if (estadoAnterior.some((filtro) => filtro.id === item.id)) {
      return estadoAnterior;
    }

    return [...estadoAnterior, item];
  });
}

function removerItemFiltro(id, setState) {
  setState((estadoAnterior) => estadoAnterior.filter((item) => item.id !== id));
}

function LinkAutomatico({ onConcluido }) {
  const { toggleMode } = useContext(DarkModeContext);
  const [exibir, setExibir] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [executando, setExecutando] = useState(false);
  const [simulou, setSimulou] = useState(false);
  const [datas, setDatas] = useState([]);
  const [favoritos, setFavoritos] = useState('todos');
  const [jaTrabalhouLocal, setJaTrabalhouLocal] = useState('todos');
  const [possuiExperiencia, setPossuiExperiencia] = useState('todos');
  const [jaPrestouServicoFuncao, setJaPrestouServicoFuncao] = useState('todos');
  const [jaAtivouApp, setJaAtivouApp] = useState('todos');
  const [maxFaltas, setMaxFaltas] = useState(100);
  const [maxCancelamentos, setMaxCancelamentos] = useState(100);
  const [notaMinima, setNotaMinima] = useState(0);
  const [distanciaMaximaKm, setDistanciaMaximaKm] = useState(50);
  const [taxaComparecimentoMinima, setTaxaComparecimentoMinima] = useState(0);
  const [quantidadeMinimaTrabalhos, setQuantidadeMinimaTrabalhos] = useState(0);
  const [clientesExcluidos, setClientesExcluidos] = useState([]);
  const [cidadesSelecionadas, setCidadesSelecionadas] = useState([]);
  const [profissionaisExcluidos, setProfissionaisExcluidos] = useState([]);
  const [resultado, setResultado] = useState(resultadoInicial);
  const [registrosSelecionados, setRegistrosSelecionados] = useState([]);

  const resumo = useMemo(() => resultado.resumo || {}, [resultado]);
  const selecionadosSet = useMemo(
    () => new Set(registrosSelecionados),
    [registrosSelecionados]
  );
  const totalSelecionados = registrosSelecionados.length;

  function abrirModal() {
    setExibir(true);
  }

  function alterarFavoritos(value) {
    setFavoritos(value || 'todos');
  }

  function alterarJaTrabalhouLocal(value) {
    setJaTrabalhouLocal(value || 'todos');
  }

  function alterarPossuiExperiencia(value) {
    setPossuiExperiencia(value || 'todos');
  }

  function alterarJaPrestouServicoFuncao(value) {
    setJaPrestouServicoFuncao(value || 'todos');
  }

  function alterarJaAtivouApp(value) {
    setJaAtivouApp(value || 'todos');
  }

  function renderFiltroSegmentado(label, value, onChange) {
    return (
      <div>
        <span>{label}</span>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '6px',
          }}
        >
          {opcoesSimNao.map((opcao) => {
            const ativo = value === opcao.value;
            let backgroundColor = ativo
              ? coresLinkAutomatico.verdePrincipal
              : undefined;
            let color = ativo ? '#fff' : undefined;

            if (!ativo && toggleMode) {
              backgroundColor = '#111827';
              color = '#dbeafe';
            }

            if (!ativo && !toggleMode) {
              backgroundColor = '#fff';
              color = coresLinkAutomatico.azulEscuro;
            }

            return (
              <button
                key={opcao.value}
                type="button"
                onClick={() => onChange(opcao.value)}
                style={{
                  minHeight: 34,
                  borderRadius: 10,
                  border: `1px solid ${
                    ativo
                      ? coresLinkAutomatico.verdePrincipal
                      : coresLinkAutomatico.bordaSuave
                  }`,
                  backgroundColor,
                  color,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {opcao.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  function resetarEstado() {
    setExibir(false);
    setCarregando(false);
    setExecutando(false);
    setSimulou(false);
    setDatas([]);
    setFavoritos('todos');
    setJaTrabalhouLocal('todos');
    setPossuiExperiencia('todos');
    setJaPrestouServicoFuncao('todos');
    setJaAtivouApp('todos');
    setMaxFaltas(100);
    setMaxCancelamentos(100);
    setNotaMinima(0);
    setDistanciaMaximaKm(50);
    setTaxaComparecimentoMinima(0);
    setQuantidadeMinimaTrabalhos(0);
    setClientesExcluidos([]);
    setCidadesSelecionadas([]);
    setProfissionaisExcluidos([]);
    setResultado(resultadoInicial);
    setRegistrosSelecionados([]);
  }

  function definirSelecaoInicial(matches = []) {
    setRegistrosSelecionados(matches.map((item) => item.id_registro));
  }

  function alternarRegistroSelecionado(idRegistro, selecionado) {
    setRegistrosSelecionados((estadoAnterior) => {
      if (selecionado) {
        if (estadoAnterior.includes(idRegistro)) {
          return estadoAnterior;
        }

        return [...estadoAnterior, idRegistro];
      }

      return estadoAnterior.filter((item) => item !== idRegistro);
    });
  }

  async function carregarSimulacao() {
    if (!Array.isArray(datas) || datas.length !== 2 || !datas[0] || !datas[1]) {
      toast.error('Selecione um intervalo de datas para simular o match.');
      return null;
    }

    setCarregando(true);

    try {
      const response = await api.post('registros/link-automatico/simular', {
        favoritos,
        jaTrabalhouLocal,
        possuiExperiencia,
        jaPrestouServicoFuncao,
        jaAtivouApp,
        maxFaltas,
        maxCancelamentos,
        notaMinima,
        distanciaMaximaKm,
        taxaComparecimentoMinima,
        quantidadeMinimaTrabalhos,
        clientesExcluidos: clientesExcluidos.map((item) => item.id),
        cidades: cidadesSelecionadas.map((item) => item.id),
        profissionaisExcluidos: profissionaisExcluidos.map((item) => item.id),
        dataInicio: format(new Date(datas[0]), 'yyyy-MM-dd'),
        dataFim: format(new Date(datas[1]), 'yyyy-MM-dd'),
      });

      setResultado(response.data);
      definirSelecaoInicial(response.data.matches || []);
      setSimulou(true);
      return response.data;
    } catch (error) {
      toast.error(error.message);
      return null;
    } finally {
      setCarregando(false);
    }
  }

  async function simularMatch() {
    await carregarSimulacao();
  }

  async function executarVinculo() {
    const vinculos = resultado.matches
      .filter((item) => selecionadosSet.has(item.id_registro))
      .map((item) => ({
        id_registro: item.id_registro,
        id_profissional: item.profissional.id_profissional,
      }));

    if (vinculos.length === 0) {
      toast.error('Selecione ao menos um match para executar o vínculo.');
      return;
    }

    setExecutando(true);

    try {
      const response = await api.post('registros/link-automatico/aplicar', {
        vinculos,
      });
      const {
        resumo: resumoExecucao,
        falhas = [],
        pedidosConfirmados = [],
      } = response.data;

      if (resumoExecucao.sucesso > 0) {
        toast.success(
          `${resumoExecucao.sucesso} vínculo(s) executado(s) com sucesso.`
        );

        if (pedidosConfirmados.length > 0) {
          toast.success(
            `${pedidosConfirmados.length} pedido(s) confirmado(s) automaticamente.`
          );
        }

        if (onConcluido) {
          await onConcluido();
        }

        await carregarSimulacao();
      }

      if (falhas.length > 0) {
        const mensagemFalhas = falhas
          .slice(0, 3)
          .map((item) =>
            item.id_registro
              ? `Registro #${item.id_registro}: ${item.erro}`
              : `Pedido #${item.id_pedido}: ${item.erro}`
          )
          .join(' | ');

        toast.error(mensagemFalhas);
      }
    } catch (error) {
      toast.error(error.message);
    }

    setExecutando(false);
  }

  return (
    <>
      <style>
        {`
          .link-automatico-modal .rs-checkbox-checked .rs-checkbox-checker label::before {
            background-color: ${coresLinkAutomatico.verdePrincipal};
            border-color: ${coresLinkAutomatico.verdePrincipal};
          }

          .link-automatico-modal .rs-picker-toggle.rs-btn:hover,
          .link-automatico-modal .rs-picker-toggle.rs-btn:focus,
          .link-automatico-modal .rs-picker-focused .rs-picker-toggle {
            border-color: ${coresLinkAutomatico.verdeBorda};
          }

          .link-automatico-modal .rs-input:focus,
          .link-automatico-modal .rs-input:hover,
          .link-automatico-modal .rs-picker-toggle-wrapper:hover,
          .link-automatico-modal .rs-picker-toggle-wrapper:focus-within {
            border-color: ${coresLinkAutomatico.verdeBorda};
          }

          .link-automatico-modal .smart-link-filtros > div {
            margin-bottom: 14px !important;
            padding: 0 0 14px;
            border: none;
            border-bottom: 1px solid ${
              toggleMode ? '#243245' : coresLinkAutomatico.bordaSuave
            };
            background: transparent;
            box-shadow: none;
          }

          .link-automatico-modal .smart-link-filtros > div:last-child {
            margin-bottom: 0 !important;
            padding-bottom: 0;
            border-bottom: none;
          }

          .link-automatico-modal .smart-link-filtros > div > span {
            display: block;
            margin-bottom: 8px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: ${toggleMode ? '#bfdbfe' : coresLinkAutomatico.azulEscuro};
          }

          .link-automatico-modal .smart-link-filtros .rs-picker-toggle,
          .link-automatico-modal .smart-link-filtros .rs-input,
          .link-automatico-modal .smart-link-filtros .rs-input-group,
          .link-automatico-modal .smart-link-filtros .rs-input-number-input {
            min-height: 36px;
            border-radius: 10px;
            border-color: ${toggleMode ? '#243245' : '#dbeafe'};
            background: ${toggleMode ? '#0f172a' : '#f8fafc'};
            box-shadow: none;
          }

          .link-automatico-modal .smart-link-filtros .rs-picker-toggle-value,
          .link-automatico-modal .smart-link-filtros .rs-input,
          .link-automatico-modal .smart-link-filtros .rs-input-number-input {
            font-size: 13px;
            color: ${toggleMode ? '#e2e8f0' : coresLinkAutomatico.azulEscuro};
          }

          .link-automatico-modal .smart-link-filtros .rs-input-group-addon {
            border-color: ${toggleMode ? '#243245' : '#dbeafe'};
            background: ${toggleMode ? '#10231a' : '#f0fdf4'};
            color: ${coresLinkAutomatico.verdeEscuro};
          }

          .link-automatico-modal .smart-link-filtros .rs-slider-bar {
            background-color: ${toggleMode ? '#1e293b' : '#dbeafe'};
          }
        `}
      </style>

      <ButtonBorderBlue
        appearance="ghost"
        darkMode={toggleMode}
        onClick={abrirModal}
        style={{
          borderColor: coresLinkAutomatico.verdePrincipal,
          color: coresLinkAutomatico.verdePrincipal,
        }}
      >
        Smart Link
      </ButtonBorderBlue>

      <Modal
        open={exibir}
        onClose={resetarEstado}
        size="full"
        className="link-automatico-modal"
      >
        <Modal.Header>
          <Modal.Title>
            <div>
              <h4 style={{ margin: 0 }}>Smart Link</h4>
              <span
                style={{ fontSize: 13, color: coresLinkAutomatico.textoSuave }}
              >
                Simule o melhor candidato por registro a partir dos filtros
                iniciais.
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '320px 1fr',
              gap: '24px',
              minHeight: '70vh',
            }}
          >
            <div
              className="smart-link-filtros"
              style={{
                border: `1px solid ${toggleMode ? '#243245' : '#dbeafe'}`,
                borderRadius: '18px',
                padding: '18px',
                backgroundColor: toggleMode ? '#1c1c24' : '#ffffff',
                boxShadow: toggleMode
                  ? 'none'
                  : '0 10px 28px rgba(29, 78, 216, 0.06)',
              }}
            >
              <h5
                style={{
                  marginTop: 0,
                  marginBottom: 16,
                  fontSize: 13,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: toggleMode
                    ? '#bfdbfe'
                    : coresLinkAutomatico.azulEscuro,
                }}
              >
                Filtros de busca
              </h5>

              <div style={{ marginBottom: 20 }}>
                <span style={{ display: 'block', marginBottom: 8 }}>Data</span>
                <InputDataRange
                  ranges={[]}
                  isoWeek
                  value={datas}
                  locale={locale}
                  onChange={setDatas}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <span style={{ display: 'block', marginBottom: 8 }}>
                  Clientes excluídos da busca
                </span>
                {clientesExcluidos.length > 0 ? (
                  <TagGroup style={{ marginBottom: 10 }}>
                    {clientesExcluidos.map((cliente) => (
                      <Tag
                        key={cliente.id}
                        closable
                        style={{
                          backgroundColor: coresLinkAutomatico.verdeClaro,
                          border: `1px solid ${coresLinkAutomatico.verdeBorda}`,
                          color: coresLinkAutomatico.verdeEscuro,
                        }}
                        onClose={() =>
                          removerItemFiltro(cliente.id, setClientesExcluidos)
                        }
                      >
                        Cliente: {cliente.label}
                      </Tag>
                    ))}
                  </TagGroup>
                ) : null}
                <FiltroEstabelecimentos
                  disabled={carregando || executando}
                  onItemAdicionado={(value, item) =>
                    adicionarItemFiltro(item, setClientesExcluidos)
                  }
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <span style={{ display: 'block', marginBottom: 8 }}>
                  Cidades
                </span>
                {cidadesSelecionadas.length > 0 ? (
                  <TagGroup style={{ marginBottom: 10 }}>
                    {cidadesSelecionadas.map((cidade) => (
                      <Tag
                        key={cidade.id}
                        closable
                        style={{
                          backgroundColor: coresLinkAutomatico.verdeClaro,
                          border: `1px solid ${coresLinkAutomatico.verdeBorda}`,
                          color: coresLinkAutomatico.verdeEscuro,
                        }}
                        onClose={() =>
                          removerItemFiltro(cidade.id, setCidadesSelecionadas)
                        }
                      >
                        Cidade: {cidade.label}
                      </Tag>
                    ))}
                  </TagGroup>
                ) : null}
                <FiltroCidades
                  disabled={carregando || executando}
                  onItemAdicionado={(value, item) =>
                    adicionarItemFiltro(item, setCidadesSelecionadas)
                  }
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <span style={{ display: 'block', marginBottom: 8 }}>
                  Excluir profissional da simulação
                </span>
                {profissionaisExcluidos.length > 0 ? (
                  <TagGroup style={{ marginBottom: 10 }}>
                    {profissionaisExcluidos.map((profissional) => (
                      <Tag
                        key={profissional.id}
                        closable
                        style={{
                          backgroundColor: coresLinkAutomatico.verdeClaro,
                          border: `1px solid ${coresLinkAutomatico.verdeBorda}`,
                          color: coresLinkAutomatico.verdeEscuro,
                        }}
                        onClose={() =>
                          removerItemFiltro(
                            profissional.id,
                            setProfissionaisExcluidos
                          )
                        }
                      >
                        Profissional: {profissional.label}
                      </Tag>
                    ))}
                  </TagGroup>
                ) : null}
                <FiltroProfissionais
                  onItemAdicionado={(value, item) =>
                    adicionarItemFiltro(item, setProfissionaisExcluidos)
                  }
                />
              </div>

              {renderFiltroSegmentado('Favoritos', favoritos, alterarFavoritos)}

              {renderFiltroSegmentado(
                'Já trabalhou no local',
                jaTrabalhouLocal,
                alterarJaTrabalhouLocal
              )}

              {renderFiltroSegmentado(
                'Possui experiência',
                possuiExperiencia,
                alterarPossuiExperiencia
              )}

              {renderFiltroSegmentado(
                'Já prestou serviço na função',
                jaPrestouServicoFuncao,
                alterarJaPrestouServicoFuncao
              )}

              {renderFiltroSegmentado(
                'Já ativou no app',
                jaAtivouApp,
                alterarJaAtivouApp
              )}

              <SmartLinkRange
                label="Máximo de faltas"
                value={maxFaltas}
                min={0}
                max={100}
                step={1}
                valueFormatter={String}
                onChange={setMaxFaltas}
              />

              <SmartLinkRange
                label="Máximo de cancelamentos"
                value={maxCancelamentos}
                min={0}
                max={100}
                step={1}
                valueFormatter={String}
                onChange={setMaxCancelamentos}
              />

              <SmartLinkRange
                label="Nota mínima"
                value={notaMinima}
                min={0}
                max={5}
                step={0.5}
                valueFormatter={(value) => value.toFixed(1)}
                minLabel="0.0"
                maxLabel="5.0"
                onChange={setNotaMinima}
              />

              <SmartLinkRange
                label="Distância máxima"
                value={distanciaMaximaKm}
                min={1}
                max={100}
                step={1}
                valueFormatter={(value) => `${value} km`}
                minLabel="1 km"
                maxLabel="100 km"
                onChange={setDistanciaMaximaKm}
              />

              <SmartLinkRange
                label="Taxa de comparecimento mínima"
                value={taxaComparecimentoMinima}
                min={0}
                max={100}
                step={1}
                valueFormatter={(value) => `${value}%`}
                minLabel="0%"
                maxLabel="100%"
                onChange={setTaxaComparecimentoMinima}
              />

              <div style={{ marginBottom: 20 }}>
                <span style={{ display: 'block', marginBottom: 8 }}>
                  Quantidade mínima de trabalhos prestados
                </span>
                <InputNumber
                  min={0}
                  step={1}
                  value={quantidadeMinimaTrabalhos}
                  style={{ width: '100%' }}
                  onChange={(value) =>
                    setQuantidadeMinimaTrabalhos(Number(value) || 0)
                  }
                />
              </div>
            </div>

            <div
              style={{
                border: `1px solid ${
                  toggleMode ? '#294235' : coresLinkAutomatico.verdeBorda
                }`,
                borderRadius: '12px',
                padding: '20px',
                backgroundColor: toggleMode ? '#1c1c24' : '#fff',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: toggleMode
                  ? 'none'
                  : '0 8px 24px rgba(22, 163, 74, 0.08)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '16px',
                }}
              >
                <div>
                  <h5 style={{ marginTop: 0, marginBottom: 4 }}>
                    Preview de resultados
                  </h5>
                  <span
                    style={{
                      fontSize: 13,
                      color: coresLinkAutomatico.textoSuave,
                    }}
                  >
                    Exibe o profissional com maior pontuação para cada registro.
                    Os matches vêm pré-selecionados para execução.
                  </span>
                </div>

                {simulou ? (
                  <div style={{ display: 'flex', gap: '16px', fontSize: 13 }}>
                    <strong>
                      {valorComFallback(resumo.totalRegistros, 0)} registros
                    </strong>
                    <strong>
                      {valorComFallback(resumo.totalMatches, 0)} matches
                    </strong>
                    <strong>
                      {valorComFallback(resumo.totalSemMatch, 0)} sem match
                    </strong>
                    <strong>{totalSelecionados} selecionados</strong>
                  </div>
                ) : null}
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {carregando ? (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Loader size="md" content="Simulando matches..." vertical />
                </div>
              ) : null}

              {!carregando && !simulou ? (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: coresLinkAutomatico.textoSuave,
                    textAlign: 'center',
                    padding: '0 40px',
                  }}
                >
                  Preencha os filtros e clique em{' '}
                  <strong style={{ marginLeft: 4 }}>Simular Match</strong>.
                </div>
              ) : null}

              {!carregando && simulou && resultado.semMatch.length > 0 ? (
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    backgroundColor: toggleMode ? '#2b1f1f' : '#fff7ed',
                    border: '1px solid #fdba74',
                    fontSize: '13px',
                  }}
                >
                  <strong>
                    {resultado.semMatch.length} registro(s) sem match.
                  </strong>
                  <div style={{ marginTop: 6 }}>
                    {resultado.semMatch.slice(0, 5).map((item) => (
                      <div key={item.id_registro}>
                        Registro #{item.id_registro} -{' '}
                        {item.profissao || 'Sem profissão'}
                      </div>
                    ))}
                    {resultado.semMatch.length > 5 ? <div>...</div> : null}
                  </div>
                </div>
              ) : null}

              {!carregando && simulou && resultado.matches.length === 0 ? (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: coresLinkAutomatico.textoSuave,
                    textAlign: 'center',
                    padding: '0 40px',
                  }}
                >
                  {resultado.semMatch.length === 0
                    ? 'Não há registros pendentes para executar com os filtros informados.'
                    : 'Nenhum profissional atendeu aos filtros informados.'}
                </div>
              ) : null}

              {!carregando && resultado.matches.length > 0 ? (
                <div
                  style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}
                >
                  {resultado.matches.map((item) => {
                    const match = obterConfiguracaoMatch(
                      item.profissional.pontuacao
                    );
                    const metricasCard = [
                      {
                        titulo: 'Faltas',
                        valor: valorComFallback(
                          item.profissional.metricas.faltas,
                          0
                        ),
                        detalhe: obterResumoFaltas(
                          item.profissional.metricas.faltas
                        ),
                      },
                      {
                        titulo: 'Nota',
                        valor: valorComFallback(
                          item.profissional.metricas.avaliacao,
                          0
                        ),
                        detalhe: obterResumoNota(
                          item.profissional.metricas.avaliacao
                        ),
                      },
                      {
                        titulo: 'Cancelamentos',
                        valor: valorComFallback(
                          item.profissional.metricas.cancelamentos,
                          0
                        ),
                        detalhe: obterResumoCancelamentos(
                          item.profissional.metricas.cancelamentos
                        ),
                      },
                      {
                        titulo: 'Distância',
                        valor:
                          item.profissional.metricas.distancia_km === null
                            ? '-'
                            : `${item.profissional.metricas.distancia_km} km`,
                        detalhe: obterResumoDistancia(
                          item.profissional.metricas.distancia_km
                        ),
                      },
                      {
                        titulo: 'Trabalhos',
                        valor: valorComFallback(
                          item.profissional.metricas.shifts,
                          0
                        ),
                        detalhe: obterResumoTrabalhos(
                          item.profissional.metricas.shifts
                        ),
                      },
                    ];

                    return (
                      <div
                        key={item.id_registro}
                        style={{
                          border: `1px solid ${
                            toggleMode ? '#294235' : '#dbeafe'
                          }`,
                          borderRadius: '18px',
                          padding: '18px 22px',
                          marginBottom: '14px',
                          backgroundColor: toggleMode ? '#15151c' : '#fff',
                          boxShadow: toggleMode
                            ? 'none'
                            : '0 10px 24px rgba(15, 23, 42, 0.06)',
                        }}
                      >
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              '34px 56px minmax(280px, 2fr) repeat(5, minmax(82px, 0.7fr)) 112px',
                            gap: '16px',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              cursor: executando ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selecionadosSet.has(item.id_registro)}
                              disabled={executando}
                              onChange={(event) =>
                                alternarRegistroSelecionado(
                                  item.id_registro,
                                  event.target.checked
                                )
                              }
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: coresLinkAutomatico.verdePrincipal,
                                cursor: executando ? 'not-allowed' : 'pointer',
                              }}
                            />
                          </div>

                          <Avatar
                            circle
                            size="lg"
                            src={item.profissional.foto_perfil}
                            alt={item.profissional.nome}
                          />

                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 20,
                                fontWeight: 700,
                                color: toggleMode ? '#f9fafb' : '#0f172a',
                                lineHeight: 1.15,
                              }}
                            >
                              {item.profissional.nome}
                            </div>
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: 14,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                color: toggleMode ? '#bfdbfe' : '#1e3a8a',
                                letterSpacing: '0.03em',
                              }}
                            >
                              {item.nome_fantasia}
                            </div>
                            <div
                              style={{
                                marginTop: 2,
                                fontSize: 14,
                                color: toggleMode
                                  ? '#86efac'
                                  : coresLinkAutomatico.verdeEscuro,
                                fontWeight: 600,
                              }}
                            >
                              {item.profissao || 'Sem profissão'}
                            </div>
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: 13,
                                color: coresLinkAutomatico.textoSuave,
                              }}
                            >
                              {formatarData(item.data)} |{' '}
                              {formatarHora(item.hora_inicio)} -{' '}
                              {formatarHora(item.hora_fim)}
                            </div>
                            <div
                              style={{
                                marginTop: 10,
                                display: 'flex',
                                gap: '6px',
                                flexWrap: 'wrap',
                              }}
                            >
                              {item.profissional.flags.favorito ? (
                                <span
                                  style={{
                                    padding: '3px 8px',
                                    borderRadius: '999px',
                                    backgroundColor: '#ecfdf5',
                                    color: coresLinkAutomatico.verdeEscuro,
                                    fontSize: 11,
                                    fontWeight: 600,
                                  }}
                                >
                                  Favorito
                                </span>
                              ) : null}
                              {item.profissional.flags.ja_trabalhou_local ? (
                                <span
                                  style={{
                                    padding: '3px 8px',
                                    borderRadius: '999px',
                                    backgroundColor: '#eff6ff',
                                    color: '#1d4ed8',
                                    fontSize: 11,
                                    fontWeight: 600,
                                  }}
                                >
                                  Já trabalhou no local
                                </span>
                              ) : null}
                              {item.profissional.flags.tem_experiencia ? (
                                <span
                                  style={{
                                    padding: '3px 8px',
                                    borderRadius: '999px',
                                    backgroundColor: '#f0fdf4',
                                    color: coresLinkAutomatico.verdeEscuro,
                                    fontSize: 11,
                                    fontWeight: 600,
                                  }}
                                >
                                  Experiente
                                </span>
                              ) : null}
                            </div>
                          </div>

                          {metricasCard.map((metrica) => (
                            <div
                              key={metrica.titulo}
                              style={{
                                minWidth: 0,
                                paddingLeft: 14,
                                borderLeft: `1px solid ${
                                  toggleMode
                                    ? 'rgba(148, 163, 184, 0.18)'
                                    : '#e5e7eb'
                                }`,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: coresLinkAutomatico.textoSuave,
                                  marginBottom: 10,
                                }}
                              >
                                {metrica.titulo}
                              </div>
                              <div
                                style={{
                                  fontSize: 20,
                                  lineHeight: 1,
                                  fontWeight: 700,
                                  color: toggleMode ? '#f8fafc' : '#0f172a',
                                }}
                              >
                                {metrica.valor}
                              </div>
                              <div
                                style={{
                                  marginTop: 8,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color:
                                    metrica.titulo === 'Distância'
                                      ? '#0ea5e9'
                                      : coresLinkAutomatico.verdePrincipal,
                                }}
                              >
                                {metrica.detalhe}
                              </div>
                            </div>
                          ))}

                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              paddingLeft: 14,
                              borderLeft: `1px solid ${
                                toggleMode
                                  ? 'rgba(148, 163, 184, 0.18)'
                                  : '#e5e7eb'
                              }`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: coresLinkAutomatico.textoSuave,
                                textTransform: 'uppercase',
                              }}
                            >
                              Match Score
                            </div>
                            <div
                              style={{
                                width: 98,
                                height: 72,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <svg
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                }}
                                width="98"
                                height="72"
                                viewBox="0 0 98 72"
                              >
                                <path
                                  d="M 16 56 A 33 33 0 0 1 82 56"
                                  fill="none"
                                  stroke={
                                    toggleMode
                                      ? 'rgba(255,255,255,0.10)'
                                      : '#dcfce7'
                                  }
                                  strokeWidth="8"
                                  strokeLinecap="round"
                                  pathLength="100"
                                  strokeDasharray="100 100"
                                />
                                <path
                                  d="M 16 56 A 33 33 0 0 1 82 56"
                                  fill="none"
                                  stroke={match.cor}
                                  strokeWidth="8"
                                  strokeLinecap="round"
                                  pathLength="100"
                                  strokeDasharray={`${match.valor} 100`}
                                />
                              </svg>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 20,
                                  fontWeight: 700,
                                  color: match.cor,
                                  marginTop: 26,
                                }}
                              >
                                {match.valor}%
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: match.cor,
                                textAlign: 'center',
                              }}
                            >
                              {match.descricao}
                            </div>
                          </div>
                        </div>

                        {item.profissional.motivos.length > 0 ? (
                          <div
                            style={{
                              marginTop: 14,
                              paddingTop: 12,
                              borderTop: `1px solid ${
                                toggleMode
                                  ? 'rgba(148, 163, 184, 0.14)'
                                  : '#eef2f7'
                              }`,
                              display: 'flex',
                              gap: '14px',
                              alignItems: 'flex-start',
                            }}
                          >
                            <strong
                              style={{
                                fontSize: 12,
                                color: coresLinkAutomatico.textoSuave,
                                minWidth: 96,
                              }}
                            >
                              Motivos
                            </strong>
                            <div
                              style={{
                                display: 'flex',
                                gap: '8px',
                                flexWrap: 'wrap',
                              }}
                            >
                              {item.profissional.motivos
                                .slice(0, 4)
                                .map((motivo) => (
                                  <span
                                    key={motivo}
                                    style={{
                                      padding: '4px 10px',
                                      borderRadius: '999px',
                                      backgroundColor: toggleMode
                                        ? '#1b2430'
                                        : '#f8fafc',
                                      color: coresLinkAutomatico.textoSuave,
                                      fontSize: 12,
                                    }}
                                  >
                                    {motivo}
                                  </span>
                                ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={resetarEstado} appearance="subtle">
            Fechar
          </Button>
          <ButtonFullBlue
            appearance="primary"
            onClick={simularMatch}
            disabled={carregando || executando}
            style={{
              backgroundColor: coresLinkAutomatico.verdeSuave,
              borderColor: coresLinkAutomatico.verdeBorda,
              color: coresLinkAutomatico.verdeEscuro,
            }}
          >
            Simular Match
          </ButtonFullBlue>
          {/* <Button appearance="ghost" disabled>
            Revisar manualmente
          </Button> */}
          <Button
            appearance="primary"
            onClick={executarVinculo}
            disabled={
              !simulou || totalSelecionados === 0 || carregando || executando
            }
            style={{
              backgroundColor: coresLinkAutomatico.verdePrincipal,
              borderColor: coresLinkAutomatico.verdePrincipal,
            }}
          >
            {executando ? 'Confirmando Link...' : 'Confirmar Link'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

LinkAutomatico.propTypes = {
  onConcluido: PropTypes.func,
};

LinkAutomatico.defaultProps = {
  onConcluido: undefined,
};

export default LinkAutomatico;
